/* eslint-disable jsx-a11y/no-static-element-interactions */
import { getLocalTimeZone } from '@internationalized/date';
import cn from 'classnames';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';

import { convertDateToAriaDateTime, extractDataAriaAttributes, useFieldId, useSyncMaxHeight } from '../../../utils';
import useControlledValue from '../../../utils/hooks/use-controlled-value';
import { Button } from '../../buttons';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon, InfoSmallIcon } from '../../foundations';
import { BaseInput, FieldLabel } from '../../internal';
import { Popup, PopupContent, PopupTrigger, Tooltip } from '../../popovers';
import { CalendarGrid } from '../calendar-grid';
import { DateRangeQuickSelections } from '../date-range-quick-selections';
import { DateSegments } from '../date-segments';
import { TimeSelector } from '../time-selector';

import { useDateTimeRangePicker } from './use-date-time-range-picker';

import type { CalendarDate, DateValue } from '@internationalized/date';
import type { ForwardedRef, KeyboardEvent, MouseEvent, ReactElement, SyntheticEvent } from 'react';

import type { DateRangeValue } from '../../../utils';
import type { DateSegmentsRef } from '../date-segments/date-segments.types';
import type { DateTimeRangePickerProps } from './date-time-range-picker.types';

import './date-time-range-picker.scss';

/**
 * DateTimeRangePicker is a composite component that allows users to select a range of dates and times.
 * It combines two DateTimeInput components for start and end values with a popup containing
 * a calendar for date selection and time selectors for precise time control.
 *
 * Built on top of React Aria's date range picker primitives with extended time support.
 * The component automatically handles keyboard navigation, accessibility, and
 * internationalization for both date and time components.
 *
 * ## Features
 *
 * - **Unified Date/Time Range Selection**: Single component for both date and time ranges
 * - **Dual Input Fields**: Separate inputs for start and end date/time
 * - **Calendar Integration**: Full calendar for date range selection
 * - **Time Selectors**: Dropdown selectors for hour, minute, and second
 * - **Granularity Control**: Configure precision from day to second level
 * - **Keyboard Navigation**: Full keyboard support with Enter/Space to open
 * - **Accessibility**: Screen reader support and proper focus management
 * - **Internationalization**: Automatic formatting based on locale
 * - **Validation**: Built-in min/max date/time validation
 * - **Form Integration**: Works seamlessly with HTML forms
 *
 * ## Usage
 *
 * ### Basic Usage
 * ```tsx
 * import { DateTimeRangePicker } from '@bloomreach/react-banana-ui';
 *
 * function MyComponent() {
 *   const [value, setValue] = useState<DateRangeValue | null>({
 *     start: new Date('2024-01-01T09:00:00'),
 *     end: new Date('2024-01-01T17:00:00')
 *   });
 *
 *   return (
 *     <DateTimeRangePicker
 *       value={value}
 *       onChange={(newValue) => setValue(newValue)}
 *       granularity="minute"
 *     />
 *   );
 * }
 * ```
 *
 * ### With Validation
 * ```tsx
 * <DateTimeRangePicker
 *   value={value}
 *   onChange={(newValue, context) => {
 *     if (context.valid) {
 *       setValue(newValue);
 *     } else {
 *       setError(context.validationMessage.join(' '));
 *     }
 *   }}
 *   minDate={new Date('2024-01-01')}
 *   maxDate={new Date('2024-12-31')}
 *   granularity="second"
 *   required
 * />
 * ```
 *
 * ### Different Granularities
 * ```tsx
 * // Date range only
 * <DateTimeRangePicker granularity="day" />
 *
 * // Date and hour range
 * <DateTimeRangePicker granularity="hour" />
 *
 * // Date, hour, and minute range (default)
 * <DateTimeRangePicker granularity="minute" />
 *
 * // Full precision with seconds
 * <DateTimeRangePicker granularity="second" />
 * ```
 *
 * ## Accessibility
 *
 * The component follows WAI-ARIA guidelines for both date and time selection:
 * - Proper focus management between inputs, calendar, and time selectors
 * - Screen reader announcements for value changes
 * - Keyboard navigation with arrow keys in calendar
 * - Escape key to close, Enter/Space to open
 * - Support for assistive technologies
 * - Proper labeling and role attributes
 *
 * @param props - The props for the DateTimeRangePicker component
 * @param forwardedRef - Ref to be forwarded to the container element
 * @returns A comprehensive date and time range picker component
 */
const DateTimeRangePicker = forwardRef(
  (props: DateTimeRangePickerProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
    const {
      className,
      clearable = false,
      defaultSelectedPresetId,
      defaultValue,
      disabled,
      error,
      granularity = 'minute',
      helperText,
      hourCycle = 12,
      id,
      label,
      onOpenChange,
      onPresetSelect,
      presets,
      readOnly,
      required,
      selectedPresetId: selectedPresetIdProp,
      tooltip,
      value,
    } = props;

    const dataAriaAttributes = extractDataAriaAttributes(props);

    const controlId = useFieldId(id, 'rbui-date-time-range-picker');

    // Refs for DOM elements
    const ref = useRef<HTMLDivElement>(null);
    const mergedRef = mergeRefs([ref, forwardedRef]);
    const dateSegmentsRef = useRef<DateSegmentsRef>(null);
    const quickSelectionsRef = useRef<HTMLUListElement>(null);

    const [lockFocus, setLockFocus] = useState(false);
    const [selectedPresetId, setSelectedPresetId] = useControlledValue<string | undefined>({
      controlledValue: selectedPresetIdProp,
      defaultValue: defaultSelectedPresetId,
      name: 'DateTimeRangePicker',
      state: 'selectedPresetId',
    });
    const [lastPresetValue, setLastPresetValue] = useState<null | { end: Date; start: Date }>(null);
    const useDateTimeRangePickerProps = useDateTimeRangePicker(props, ref);

    const {
      calendarProps: {
        calendarProps,
        calendarRef,
        calendarState,
        leftTitle,
        nextButtonProps,
        prevButtonProps,
        rightTitle,
      },
      dateFieldProps: {
        dateFieldEndInputRef,
        dateFieldEndProps,
        dateFieldEndRef,
        dateFieldEndState,
        hiddenInputEndProps,

        dateFieldStartInputRef,
        dateFieldStartProps,
        dateFieldStartRef,
        dateFieldStartState,
        hiddenInputStartProps,

        groupProps,
      },
      dateRangePickerState,
      popupProps: { dialogProps },
      timeFieldProps: { onTimeChange, timeValue },
    } = useDateTimeRangePickerProps;

    const quickSelectionsEnabled = Boolean(dateRangePickerState.isOpen && presets && presets.length > 0);

    useSyncMaxHeight(calendarRef, quickSelectionsRef, {
      enabled: quickSelectionsEnabled,
      property: 'maxHeight',
      round: true,
    });

    // Apply the initial preset's date range on mount when no explicit value/defaultValue is provided
    const hasAppliedInitialPreset = useRef(false);

    useEffect(() => {
      if (hasAppliedInitialPreset.current) {
        return;
      }

      const initialPresetId = selectedPresetIdProp ?? defaultSelectedPresetId;

      if (!initialPresetId || !presets?.length) {
        return;
      }

      if (value !== undefined || defaultValue !== undefined) {
        hasAppliedInitialPreset.current = true;

        return;
      }

      const preset = presets.find((p) => p.id === initialPresetId && !p.disabled);

      if (preset) {
        const val = preset.getValue();

        dateRangePickerState.setValue({
          end: convertDateToAriaDateTime(val.end),
          start: convertDateToAriaDateTime(val.start),
        });
        setLastPresetValue({ end: val.end, start: val.start });
        hasAppliedInitialPreset.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (!dateRangePickerState.value?.start || !dateRangePickerState.value?.end) {
        return;
      }

      // If we have a last preset value, check if current value matches it
      if (lastPresetValue && selectedPresetId) {
        const currentStart = dateRangePickerState.value.start.toDate(getLocalTimeZone());
        const currentEnd = dateRangePickerState.value.end.toDate(getLocalTimeZone());

        // Compare timestamps to see if the range has changed
        const startMatches = currentStart.getTime() === lastPresetValue.start.getTime();
        const endMatches = currentEnd.getTime() === lastPresetValue.end.getTime();

        // If either date changed, clear the preset selection
        if (!startMatches || !endMatches) {
          setSelectedPresetId(undefined);
          // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
          setLastPresetValue(null);
        }
      }
    }, [dateRangePickerState.value, lastPresetValue, selectedPresetId]);

    const handleOpenChange = (o: boolean): void => {
      onOpenChange?.(o);
      dateRangePickerState.setOpen(o);

      if (!o) {
        setLockFocus(false);

        if (
          dateRangePickerState.value?.start &&
          dateRangePickerState.value?.end &&
          dateRangePickerState.value.start > dateRangePickerState.value.end
        ) {
          dateRangePickerState.setValue({
            ...dateRangePickerState.value,
            end: dateRangePickerState.value.start,
            start: dateRangePickerState.value.end,
          } as DateRangeValue<DateValue>);
        }

        calendarState.setFocusedDate(dateRangePickerState.value.end as CalendarDate);
      }
    };

    const handleClick = (): void => {
      dateRangePickerState.open();
      onOpenChange?.(true);
      calendarState.setFocused(false);
      setLockFocus(false);
    };

    const handleDateSegmentKeyDown = (e: KeyboardEvent<HTMLElement>, dateType: 'end' | 'start'): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        const date = dateRangePickerState.value[dateType] as CalendarDate;
        dateRangePickerState.open();
        onOpenChange?.(true);
        calendarState.setFocusedDate(date);
        calendarState.setFocused(true);
        setLockFocus(true);
      }
    };

    const handlePresetSelect = (presetId: string, value: DateRangeValue): void => {
      setSelectedPresetId(presetId);
      // Store the preset value so we can detect manual changes later
      setLastPresetValue({
        end: value.end,
        start: value.start,
      });

      // Convert Date objects to DateValue for the date range picker state
      const startDateValue = convertDateToAriaDateTime(value.start);
      const endDateValue = convertDateToAriaDateTime(value.end);

      dateRangePickerState.setValue({
        end: endDateValue,
        start: startDateValue,
      } as DateRangeValue<DateValue>);

      // Call the optional onPresetSelect callback
      onPresetSelect?.(presetId, value);
    };

    const handleClear = (e: SyntheticEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      dateRangePickerState.setValue(null);
      calendarState.setValue(null);
      setSelectedPresetId(undefined);
      setLastPresetValue(null);
    };

    const getClearButton = (): ReactElement => {
      const hasValue = !!(dateRangePickerState.value?.start && dateRangePickerState.value?.end);
      const hideClear = disabled || readOnly || !hasValue;

      return (
        <Button
          aria-label="Clear"
          className={cn('rbui-base-input__clear-button', {
            'rbui-base-input__clear-button--hidden': hideClear,
          })}
          onClick={handleClear}
          // Do not trigger onBlur when clear input
          onMouseDown={(e) => e.preventDefault()}
          startIcon={<CloseIcon />}
          type="tertiary"
        />
      );
    };

    const hasLabel = Boolean(label);
    const hasHelper = Boolean(helperText);

    const fieldLabel = label && (
      <FieldLabel htmlFor={controlId} required={required}>
        {label === ' ' ? <span>&nbsp;</span> : label}
      </FieldLabel>
    );

    const control = (
      <Popup onOpenChange={handleOpenChange} open={dateRangePickerState.isOpen}>
        <PopupTrigger>
          <BaseInput
            className={cn('rbui-date-time-range-input', className)}
            disabled={disabled}
            error={error}
            id={controlId}
            leftElement={
              <CalendarIcon
                className="rbui-date-time-range-picker__calendar-icon"
                onClick={() => {
                  dateSegmentsRef.current?.focusFirstSegment();
                }}
              />
            }
            readOnly={readOnly}
            ref={mergedRef}
            renderInput={(inputProps) => {
              return (
                <span {...inputProps}>
                  <span
                    ref={dateFieldStartRef}
                    {...dateFieldStartProps}
                    onClick={(e: MouseEvent<HTMLElement>) => {
                      dateFieldStartProps.onClick?.(e);
                      handleClick();
                    }}
                    onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
                      dateFieldStartProps.onKeyDown?.(e);
                      handleDateSegmentKeyDown(e, 'start');
                    }}
                  >
                    <DateSegments ref={dateSegmentsRef} state={dateFieldStartState} />
                    <input {...hiddenInputStartProps} ref={dateFieldStartInputRef} />
                  </span>
                  <span>&nbsp;-&nbsp;</span>
                  <span
                    ref={dateFieldEndRef}
                    {...dateFieldEndProps}
                    onClick={(e: MouseEvent<HTMLElement>) => {
                      dateFieldEndProps.onClick?.(e);
                      handleClick();
                    }}
                    onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
                      dateFieldEndProps.onKeyDown?.(e);
                      handleDateSegmentKeyDown(e, 'end');
                    }}
                  >
                    <DateSegments state={dateFieldEndState} />
                    <input {...hiddenInputEndProps} ref={dateFieldEndInputRef} />
                  </span>
                </span>
              );
            }}
            required={required}
            rightElement={clearable && getClearButton()}
            {...groupProps}
            onClick={(e: MouseEvent<HTMLElement>) => {
              groupProps.onClick?.(e);
              handleClick();
            }}
            {...dataAriaAttributes}
          />
        </PopupTrigger>
        <PopupContent asChild initialFocus={lockFocus ? 0 : -1} modalFocus={true}>
          <div className={cn('rbui-date-time-range-picker__popup-content')} ref={mergedRef} {...dialogProps}>
            <div className="rbui-date-time-range-picker__content" ref={calendarRef} {...calendarProps}>
              <header className="rbui-date-time-range-picker__calendar-header">
                <Button {...prevButtonProps} startIcon={<ChevronLeftIcon />} type="tertiary" />
                <span className="rbui-date-time-range-picker__calendar-header-title">{leftTitle}</span>
                <span className="rbui-date-time-range-picker__calendar-header-title">{rightTitle}</span>
                <Button {...nextButtonProps} startIcon={<ChevronRightIcon />} type="tertiary" />
              </header>

              <div className="rbui-date-time-range-picker__calendar-body">
                <div className="rbui-date-time-range-picker__left-column">
                  <div className="rbui-date-time-range-picker__left-calendar">
                    <CalendarGrid state={calendarState} />
                  </div>

                  {granularity !== 'day' && (
                    <TimeSelector
                      className="rbui-date-time-range-picker__left-time-selector"
                      disabled={disabled}
                      granularity={granularity}
                      hourCycle={hourCycle}
                      key="start"
                      onChange={(newValue) => onTimeChange('start', newValue)}
                      rangeType="start"
                      readOnly={readOnly}
                      value={timeValue.start}
                    />
                  )}
                </div>

                <hr className="rbui-date-time-range-picker__separator" />

                <div className="rbui-date-time-range-picker__right-column">
                  <div className="rbui-date-time-range-picker__right-calendar">
                    <CalendarGrid offset={1} state={calendarState} />
                  </div>

                  {granularity !== 'day' && (
                    <TimeSelector
                      className="rbui-date-time-range-picker__right-time-selector"
                      disabled={disabled}
                      granularity={granularity}
                      hourCycle={hourCycle}
                      key="end"
                      onChange={(newValue) => onTimeChange('end', newValue)}
                      rangeType="end"
                      readOnly={readOnly}
                      value={timeValue.end}
                    />
                  )}
                </div>
              </div>
            </div>

            {presets && presets.length > 0 && (
              <DateRangeQuickSelections
                className="rbui-date-time-range-picker__quick-selections"
                onPresetSelect={handlePresetSelect}
                presets={presets}
                ref={quickSelectionsRef}
                selectedPresetId={selectedPresetId}
              />
            )}
          </div>
        </PopupContent>
      </Popup>
    );

    return (
      <div
        className={cn('rbui-date-time-range-picker-field', className, {
          'rbui-date-time-range-picker-field--error': error,
          'rbui-date-time-range-picker-field--has-helper': hasHelper,
          'rbui-date-time-range-picker-field--has-label': hasLabel,
        })}
      >
        {hasLabel && (
          <div className="rbui-date-time-range-picker-field__label">
            {fieldLabel}
            {tooltip && (
              <Tooltip title={tooltip}>
                <InfoSmallIcon className="rbui-date-time-range-picker-field__icon" />
              </Tooltip>
            )}
          </div>
        )}
        <div className="rbui-date-time-range-picker-field__control">{control}</div>
        {hasHelper && <span className="rbui-date-time-range-picker-field__helper">{helperText}</span>}
      </div>
    );
  },
);

DateTimeRangePicker.displayName = 'DateTimeRangePicker';

export default DateTimeRangePicker;
